import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { Subscription, filter, first } from 'rxjs';
import { MeetingParticipantsComponent } from '@features/dashboard/meetings/components/meeting-participants/meeting-participants.component';
import { MeetingStatusTagComponent } from '@features/dashboard/meetings/components/meeting-status-tag/meeting-status-tag.component';
import {
  formatarDuracaoDaLigacao,
  mensagemDoErro,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { ModalMeetingRecordingComponent } from '@features/dashboard/meetings/components/modal-meeting-recording/modal-meeting-recording.component';
import { ModalMeetingTranscriptComponent } from '@features/dashboard/meetings/components/modal-meeting-transcript/modal-meeting-transcript.component';

/**
 * Aba **Ligações** da tela do deal (card 3112).
 *
 * As reuniões do Teams dos usuários Legatus com participantes da empresa do deal, gravadas e
 * transcritas pelo Recall.ai, vinculadas ao deal pela rotina automática ou à mão (Comercial ›
 * Ligações).
 *
 * **A aba é do time interno e do parceiro do deal**, com gravação e transcrição (decisão do Breno em
 * 29/09/2026). O parceiro vê só as reuniões de que participou; o time interno, as vinculadas que ele
 * veria na visão geral (regra de 07/10/2026: call interna só para quem estava; o servidor recorta). "Ver detalhes" abre a tela de detalhe pela rota do deal. Usuário de empresa não vê: ele não chega à tela do deal (`COMMERCIAL_MY_DEALS` só existe
 * para PARTNER e TENANT). Só o **Desvincular** é do time interno, e quem decide isso é a
 * tela do deal (`podeDesvincular`, a partir do `canDecide` do servidor).
 *
 * Sub-componente de propósito: o `deal-detail.component.ts` já passa de mil linhas. O estado é
 * todo daqui, zerado a cada troca de `dealId` — e a tela do deal volta para a Visão geral ao
 * trocar de deal, o que destrói este painel de qualquer modo.
 *
 * ⚠️ Falha ao carregar fica **dentro** do painel, com "Tentar de novo": não derruba a tela do deal.
 */
@Component({
  selector: 'app-deal-meetings-panel',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MeetingParticipantsComponent,
    MeetingStatusTagComponent,
    ModalMeetingRecordingComponent,
    ModalMeetingTranscriptComponent,
  ],
  templateUrl: './deal-meetings-panel.component.html',
  styleUrl: './deal-meetings-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealMeetingsPanelComponent extends VaultBaseComponent implements OnChanges {
  readonly dealId = input.required<string>();
  /** Time interno: mostra o Desvincular. Vem do `canDecide` do servidor, via tela do deal. */
  readonly podeDesvincular = input(false);

  @ViewChild('modalGravacao') modalGravacao: ModalMeetingRecordingComponent | null = null;
  @ViewChild('modalTranscricao') modalTranscricao: ModalMeetingTranscriptComponent | null = null;

  protected readonly ligacoes = signal<MeetingDto[]>([]);
  protected readonly carregando = signal(false);
  protected readonly erro = signal<string | null>(null);
  /** Ligação sendo desvinculada — trava só o botão dela. */
  protected readonly desvinculando = signal<string | null>(null);

  protected readonly duracao = formatarDuracaoDaLigacao;

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private carga?: Subscription;

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly confirmationService: ConfirmationService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dealId']) {
      this.reiniciar();
      this.carregar();
    }
  }

  /** Zera o que era do deal anterior, inclusive uma carga ainda em voo. */
  private reiniciar(): void {
    this.carga?.unsubscribe();
    this.ligacoes.set([]);
    this.erro.set(null);
    this.desvinculando.set(null);
  }

  carregar(): void {
    const dealId = this.dealId();
    if (!dealId) return;

    this.carga?.unsubscribe();
    this.carregando.set(true);
    this.erro.set(null);

    this.carga = this.meetingsService
      .getDealMeetings(dealId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: meetings => {
          this.carregando.set(false);
          // O backend já ordena; reordenar aqui é defesa barata contra uma mudança de lá.
          this.ligacoes.set(
            [...(meetings ?? [])].sort((a, b) =>
              b.scheduledStartAt.localeCompare(a.scheduledStartAt)
            )
          );
        },
        error: erro => {
          this.carregando.set(false);
          this.erro.set(mensagemDoErro(erro, 'Não foi possível carregar as reuniões deste deal.'));
        },
      });
  }

  /** Abre a tela de detalhe pela rota do deal (card 3162): vale também para o parceiro. */
  protected abrirDetalhe(meeting: MeetingDto): void {
    this.router.navigate(['/dashboard/commercial/my-deals', this.dealId(), 'meetings', meeting.id]);
  }

  protected assistir(meeting: MeetingDto): void {
    this.modalGravacao?.open(meeting, this.dealId());
  }

  protected verTranscricao(meeting: MeetingDto): void {
    this.modalTranscricao?.open(meeting, this.dealId());
  }

  protected desvincular(meeting: MeetingDto): void {
    const dealId = this.dealId();
    this.confirmationService
      .confirm(
        'Desvincular esta reunião do deal?',
        `"${meeting.title}" deixa de aparecer na aba Reuniões deste deal para todos que o veem. A reunião continua em Comercial › Meetings e pode ser vinculada de novo.`,
        'danger',
        undefined,
        'Desvincular'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() => {
        this.desvinculando.set(meeting.id);
        this.meetingsService
          .unlinkDeal(meeting.id, dealId)
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.desvinculando.set(null);
              this.toast.showToastSuccess('Reunião desvinculada do deal.');
              this.carregar();
            },
            error: erro => {
              this.desvinculando.set(null);
              this.toast.showToastError(
                'Não foi possível desvincular',
                mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
              );
            },
          });
      });
  }
}
